import type { CostLine, Deadline, DeadlineOwner } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { formatMoney, useLocale, useT } from '../lib/i18n.js'
import { Button, Card, ErrorNotice, Field, Input, Select } from './ui.js'

/**
 * Deadlines and money — build unit 7.8. "What needs you, by when," plus the
 * cost lines that back some of those deadlines, rolled up into a running total.
 *
 * Structured like `PlanEditor.tsx`: one query for the whole summary, mutations
 * that invalidate it, every string through `t`. The one rule worth restating
 * here because a display component is exactly where it would leak: totals are
 * per currency, computed by the server, and NEVER combined into one number —
 * a trip billed in two currencies renders two totals, side by side.
 */
export function MoneyCard({ tripId }: { tripId: string }) {
  const t = useT()
  const queryClient = useQueryClient()

  const summary = useQuery({
    queryKey: ['money', tripId],
    queryFn: () => api.getMoneySummary(tripId),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['money', tripId] })

  return (
    <Card title={t('money.title')} collapsible>
      <div className="flex flex-col gap-6">
        {summary.isError && <ErrorNotice error={summary.error} />}
        {summary.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}

        {summary.data && (
          <section>
            <h3 className="text-sm font-semibold">{t('money.totals.title')}</h3>
            {summary.data.totals.length === 0 ? (
              <p className="mt-1 text-sm text-slate-500">{t('money.totals.empty')}</p>
            ) : (
              <ul className="mt-1 flex flex-wrap gap-4">
                {summary.data.totals.map((total) => (
                  <li
                    key={total.currency}
                    className="rounded-md bg-slate-50 px-3 py-2 text-lg font-semibold"
                    data-testid={`money-total-${total.currency}`}
                  >
                    <TotalAmount amountMinor={total.totalMinor} currency={total.currency} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {summary.data && (
          <DeadlinesSection
            tripId={tripId}
            deadlines={summary.data.deadlines}
            costLines={summary.data.costLines}
            onChange={invalidate}
          />
        )}

        {summary.data && (
          <CostLinesSection tripId={tripId} costLines={summary.data.costLines} onChange={invalidate} />
        )}
      </div>
    </Card>
  )
}

function TotalAmount({ amountMinor, currency }: { amountMinor: number; currency: string }) {
  const { locale } = useLocale()
  return <>{formatMoney(amountMinor, currency, locale)}</>
}

// ---------------------------------------------------------------------------
// Deadlines: "what needs you, by when"
// ---------------------------------------------------------------------------

function DeadlinesSection({
  tripId,
  deadlines,
  costLines,
  onChange,
}: {
  tripId: string
  deadlines: Deadline[]
  costLines: CostLine[]
  onChange(): Promise<void> | void
}) {
  const t = useT()
  const [adding, setAdding] = useState(false)

  const create = useMutation({
    mutationFn: (form: FormData) =>
      api.createDeadline(tripId, {
        label: String(form.get('label')),
        dueAt: instantFromLocal(String(form.get('dueAt'))),
        owner: String(form.get('owner')) as DeadlineOwner,
        ...optionalText(form, 'costLineId'),
      }),
    async onSuccess() {
      await onChange()
      setAdding(false)
    },
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'open' | 'done' }) =>
      api.updateDeadline(id, { status }),
    onSuccess: onChange,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteDeadline(id),
    onSuccess: onChange,
  })

  return (
    <section className="flex flex-col gap-3 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('money.deadlines.title')}</h3>
        <Button variant="quiet" onClick={() => setAdding((v) => !v)}>
          {t('money.deadlines.add')}
        </Button>
      </div>

      {deadlines.length === 0 && <p className="text-sm text-slate-500">{t('money.deadlines.empty')}</p>}

      <ul className="flex flex-col divide-y divide-slate-200">
        {deadlines.map((deadline) => (
          <li key={deadline.id} className="flex items-center justify-between gap-4 py-3">
            <div className="flex flex-col">
              <span className={`font-medium ${deadline.status === 'done' ? 'line-through text-slate-400' : ''}`}>
                {deadline.label}
              </span>
              <span className="text-xs text-slate-500">
                <DueAt value={deadline.dueAt} />
                {' · '}
                {t(`money.deadlines.owner.${deadline.owner}`)}
                {' · '}
                {t(`money.deadlines.status.${deadline.status}`)}
                {deadline.costLineId && costLineLabel(costLines, deadline.costLineId)}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="quiet"
                disabled={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({
                    id: deadline.id,
                    status: deadline.status === 'done' ? 'open' : 'done',
                  })
                }
              >
                {deadline.status === 'done' ? t('money.deadlines.reopen') : t('money.deadlines.markDone')}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm(t('money.removeConfirm', { label: deadline.label }))) {
                    remove.mutate(deadline.id)
                  }
                }}
              >
                {t('common.remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {adding && (
        <form
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            create.mutate(new FormData(event.currentTarget))
          }}
          className="flex flex-col gap-3 rounded-md bg-slate-50 p-3"
        >
          <Field
            label={t('money.deadlines.field.label')}
            guidance="money.deadlines.field.label.guidance"
          >
            <Input name="label" required maxLength={200} autoFocus />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('money.deadlines.field.dueAt')}
              guidance="money.deadlines.field.dueAt.guidance"
            >
              <Input name="dueAt" type="datetime-local" required />
            </Field>
            <Field
              label={t('money.deadlines.field.owner')}
              guidance="money.deadlines.field.owner.guidance"
            >
              <Select name="owner" defaultValue="traveller">
                <option value="traveller">{t('money.deadlines.owner.traveller')}</option>
                <option value="advisor">{t('money.deadlines.owner.advisor')}</option>
              </Select>
            </Field>
          </div>
          {costLines.length > 0 && (
            <Field label={t('money.costLines.title')}>
              <Select name="costLineId" defaultValue="">
                <option value="">{t('common.none')}</option>
                {costLines.map((line) => (
                  <option key={line.id} value={line.id}>
                    {line.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {create.isError && <ErrorNotice error={create.error} />}
          <div className="flex gap-2">
            <Button type="submit" disabled={create.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="quiet" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}

function DueAt({ value }: { value: string }) {
  const { locale } = useLocale()
  return (
    <>
      {new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )}
    </>
  )
}

function costLineLabel(costLines: CostLine[], id: string): string {
  const match = costLines.find((line) => line.id === id)
  return match ? ` · ${match.label}` : ''
}

// ---------------------------------------------------------------------------
// Cost lines
// ---------------------------------------------------------------------------

function CostLinesSection({
  tripId,
  costLines,
  onChange,
}: {
  tripId: string
  costLines: CostLine[]
  onChange(): Promise<void> | void
}) {
  const t = useT()
  const { locale } = useLocale()
  const [adding, setAdding] = useState(false)

  const create = useMutation({
    mutationFn: (form: FormData) =>
      api.createCostLine(tripId, {
        label: String(form.get('label')),
        amountMinor: minorUnitsFromMajor(String(form.get('amount'))),
        currency: String(form.get('currency')).toUpperCase(),
        status: 'quoted',
      }),
    async onSuccess() {
      await onChange()
      setAdding(false)
    },
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CostLine['status'] }) =>
      api.updateCostLine(id, { status }),
    onSuccess: onChange,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCostLine(id),
    onSuccess: onChange,
  })

  return (
    <section className="flex flex-col gap-3 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('money.costLines.title')}</h3>
        <Button variant="quiet" onClick={() => setAdding((v) => !v)}>
          {t('money.costLines.add')}
        </Button>
      </div>

      {costLines.length === 0 && <p className="text-sm text-slate-500">{t('money.costLines.empty')}</p>}

      <ul className="flex flex-col divide-y divide-slate-200">
        {costLines.map((line) => (
          <li key={line.id} className="flex items-center justify-between gap-4 py-3">
            <div className="flex flex-col">
              <span className="font-medium">{line.label}</span>
              <span className="text-xs text-slate-500">
                {formatMoney(line.amountMinor, line.currency, locale)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={line.status}
                disabled={setStatus.isPending}
                onChange={(e) =>
                  setStatus.mutate({ id: line.id, status: e.target.value as CostLine['status'] })
                }
              >
                <option value="quoted">{t('money.costLines.status.quoted')}</option>
                <option value="committed">{t('money.costLines.status.committed')}</option>
                <option value="paid">{t('money.costLines.status.paid')}</option>
              </Select>
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm(t('money.removeConfirm', { label: line.label }))) {
                    remove.mutate(line.id)
                  }
                }}
              >
                {t('common.remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {adding && (
        <form
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            create.mutate(new FormData(event.currentTarget))
          }}
          className="flex flex-col gap-3 rounded-md bg-slate-50 p-3"
        >
          <Field
            label={t('money.costLines.field.label')}
            guidance="money.costLines.field.label.guidance"
          >
            <Input name="label" required maxLength={200} autoFocus />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('money.costLines.field.amount')}
              guidance="money.costLines.field.amount.guidance"
            >
              <Input name="amount" inputMode="decimal" placeholder="1999.90" required />
            </Field>
            <Field
              label={t('money.costLines.field.currency')}
              guidance="money.costLines.field.currency.guidance"
            >
              <Input name="currency" maxLength={3} placeholder="BRL" required className="uppercase" />
            </Field>
          </div>
          {create.isError && <ErrorNotice error={create.error} />}
          <div className="flex gap-2">
            <Button type="submit" disabled={create.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="quiet" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}

/** Empty inputs are omitted rather than sent as '', which would fail validation. */
function optionalText(form: FormData, key: string): Record<string, string> {
  const value = String(form.get(key) ?? '').trim()
  return value ? { [key]: value } : {}
}

/**
 * `datetime-local` yields a wall-clock string with no zone. Interpreted here as
 * the browser's own zone and sent as an instant, same convention as
 * `PlanEditor.tsx`'s `optionalInstant` — "the advisor typed this while looking
 * at their own clock."
 */
function instantFromLocal(value: string): string {
  return new Date(value).toISOString()
}

/**
 * The ONE place a display-facing string becomes an integer minor-units amount.
 * Everything downstream of this — the request body, the server, the database,
 * the response, every other component — only ever sees the integer.
 */
function minorUnitsFromMajor(value: string): number {
  const parsed = Number(value)
  return Math.round(parsed * 100)
}
